import type { TaskChip } from '../lib/taskBuckets';
import { formatTaskDate } from '../lib/taskBuckets';
import { cn } from '@/lib/utils';

type Kpis = { overdue: number; today: number; week: number; done: number };

interface TaskKpiRowProps {
  kpis: Kpis;
  onSelectChip: (chip: TaskChip) => void;
  activeChip?: TaskChip;
}

const cards: {
  key: TaskChip;
  label: string;
  valueKey: keyof Kpis;
  accentClass: string;
  valueClass: string;
  subtitle: () => string;
}[] = [
  {
    key: 'overdue',
    label: 'Atrasadas',
    valueKey: 'overdue',
    accentClass: 'kpi-accent--danger',
    valueClass: 'text-error',
    subtitle: () => 'Requer atenção imediata',
  },
  {
    key: 'today',
    label: 'Para hoje',
    valueKey: 'today',
    accentClass: 'kpi-accent--warning',
    valueClass: 'text-warning',
    subtitle: () => `Vence em ${formatTaskDate(new Date().toISOString())}`,
  },
  {
    key: 'week',
    label: 'Esta semana',
    valueKey: 'week',
    accentClass: '',
    valueClass: 'text-primary',
    subtitle: () => 'Próximos 7 dias',
  },
  {
    key: 'done',
    label: 'Concluídas',
    valueKey: 'done',
    accentClass: 'kpi-accent--success',
    valueClass: 'text-success',
    subtitle: () => 'Total concluídas',
  },
];

/** KPI compacto (kpi-mini do mock) — mais denso que metric-value do dashboard. */
export function TaskKpiRow({ kpis, onSelectChip, activeChip }: TaskKpiRowProps) {
  return (
    <section aria-label="Resumo de tarefas">
      <ul className="grid grid-cols-1 gap-s3 md:grid-cols-4 md:gap-s4">
        {cards.map(card => {
          const isActive = activeChip === card.key;
          return (
            <li key={card.key}>
              <button
                type="button"
                onClick={() => onSelectChip(isActive ? 'all' : card.key)}
                aria-pressed={isActive}
                className={cn(
                  'relative w-full touch-manipulation overflow-hidden rounded-lg border bg-card px-s3 py-s3 text-left shadow-xs transition-base hover:shadow-sm md:px-s4 md:py-s4',
                  'motion-reduce:transition-none',
                  isActive ? 'border-primary/40 shadow-sm' : 'border-border',
                )}
              >
                <span className={cn('kpi-accent', card.accentClass)} aria-hidden />
                <p className="mb-s2 text-xs uppercase text-muted">
                  {card.label}
                </p>
                <p
                  className={cn(
                    'font-mono text-kpi-sm font-semibold leading-none tracking-tight tabular-nums',
                    card.valueClass,
                  )}
                >
                  {kpis[card.valueKey]}
                </p>
                <p className="mt-s1 text-caption">{card.subtitle()}</p>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
